import { reactive } from "vue";

const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || "https://177.36.4.221:3000";
const concurrentPages = 3;

export const csvExportState = reactive({
  status: "idle",
  completedPages: 0,
  totalPages: 0,
  downloadUrl: "",
  error: "",
});

export async function fetchCNPJPage(request, page, secureFetch) {
  const response = await secureFetch(`${apiBaseUrl}/search-cnpj`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...request, page }),
  });

  if (!response?.ok)
    throw new Error("Não foi possível carregar os resultados da pesquisa.");

  const data = await response.json();
  if (!Array.isArray(data.elements))
    throw new Error("Resposta inválida da pesquisa de CNPJ.");

  return data;
}

const excelTextFields = new Set([
  "COD_CNPJ",
  "TELEFONE_1",
  "TELEFONE_2",
  "CEP",
  "CNAE_FISCAL",
  "NATUREZA_JURIDICA",
  "DATA_INICIO_ATIVIDADE",
  "CAPITAL_SOCIAL_EMPRESA",
]);

function formatCSV(value, field) {
  const scalar = value != null && typeof value === "object" ? JSON.stringify(value) : value;
  let text = String(scalar ?? "").normalize("NFC");

  if (text && excelTextFields.has(field))
    text = `="${text.replace(/"/g, '""')}"`;

  return `"${text.replace(/"/g, '""')}"`;
}

export function downloadCSV() {
  if (!csvExportState.downloadUrl) return;

  const link = document.createElement("a");

  link.href = csvExportState.downloadUrl;
  link.download = "pesquisa_cnpj.csv";

  document.body.appendChild(link);

  try {
    link.click();
  } finally {
    link.remove();
  }
}

export function dismissCSVExport() {
  if (csvExportState.status === "running") return;

  if (csvExportState.downloadUrl)
    URL.revokeObjectURL(csvExportState.downloadUrl);

  csvExportState.downloadUrl = "";
  csvExportState.status = "idle";
  csvExportState.error = "";
}

export async function startCSVExport({ request, pageCount, totalPages, secureFetch }) {
  if (csvExportState.status === "running") return;

  const invalidPageNumber = !request || !Number.isInteger(pageCount) || pageCount < 1 || pageCount > totalPages

  if (invalidPageNumber)
    throw new Error("Quantidade de páginas inválida.");

  const savedRequest = JSON.parse(JSON.stringify(request));
  dismissCSVExport();

  csvExportState.status = "running";
  csvExportState.completedPages = 0;
  csvExportState.totalPages = pageCount;

  try {
    const firstPage = await fetchCNPJPage(savedRequest, 0, secureFetch);
    
    if (!firstPage.elements.length) 
      throw new Error("Nenhum dado encontrado para exportar.");
    
    const headers = Object.keys(firstPage.elements[0]);
    const chunks = ["\uFEFF", headers.map(formatCSV).join(";")];

    const appendPage = async (data) => {
      for (const row of data.elements) {
        chunks.push("\r\n", headers.map(field => formatCSV(row[field], field)).join(";"));
      }
      csvExportState.completedPages++;
      await new Promise(resolve => setTimeout(resolve, 0));
    };

    await appendPage(firstPage);
    for (let page = 1; page < pageCount; page += concurrentPages) {
      const batchSize = Math.min(concurrentPages, pageCount - page);
      const pages = await Promise.all(Array.from({ length: batchSize }, (_, index) =>
        fetchCNPJPage(savedRequest, page + index, secureFetch)
      ));
      for (const data of pages) await appendPage(data);
    }

    const blob = new Blob(chunks, { type: "text/csv;charset=utf-8;" });
    
    csvExportState.downloadUrl = URL.createObjectURL(blob);
    downloadCSV();
    csvExportState.status = "success";
  } catch (error) {
    console.error("Erro ao exportar o CSV:", error);
    
    csvExportState.error = "Não foi possível exportar o CSV. Volte à pesquisa de CNPJ e tente novamente.";
    csvExportState.status = "error";
  }
}
